import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import toast from 'shared/utils/toast';
import { useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { MODELS_MAPS } from '../config/modelsMap';
import { Option } from 'shared/types/general';
import {
  EnhancedForm,
  IdentityField,
  TextFieldAdapter,
  AutocompleteAdapter
} from 'components/enhanced';

interface FormParams {
  models: Option[];
  onSubmit: any;
}

interface AdditionalField {
  name: string;
  code: string;
  options: Option[];
  optionLabelField?: string;
  optionValueField?: string;
}

function FormActions({
  searchLabel,
  onResetExtra
}: {
  searchLabel: string;
  onResetExtra?: () => void;
}) {
  const form = useForm();
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row">
      <Button
        type="button"
        color="error"
        variant="outlined"
        className="w-full"
        onClick={() => {
          form.reset();
          onResetExtra?.();
        }}>
        {t('actions.reset')}
      </Button>
      <Button type="submit" variant="contained" className="w-full">
        {searchLabel}
      </Button>
    </div>
  );
}

function LogsFilterForm(params: FormParams) {
  const { models, onSubmit } = params;
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [objectOptions, setObjectOptions] = useState<Option[]>([]);
  const [optionLabelField, setOptionLabelField] = useState<string | undefined>(undefined);
  const [additionalFilter, setAdditionalFilter] = useState<AdditionalField[]>([]);
  const [objectOptionsLoading, setObjectOptionsLoading] = useState(false);
  const [additionalFieldsLoading, setAdditionalFieldsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    setObjectOptions([]);
    setAdditionalFilter([]);
    setObjectOptionsLoading(false);
    setAdditionalFieldsLoading(false);

    if (!selectedModel) {
      setOptionLabelField(undefined);
      return;
    }

    const modelConfig = MODELS_MAPS?.[selectedModel.code];
    if (!modelConfig) {
      setOptionLabelField(undefined);
      return;
    }

    if (modelConfig.optionLabelField) {
      setOptionLabelField(modelConfig.optionLabelField);
    } else {
      setOptionLabelField(undefined);
    }

    if (modelConfig.fields) {
      setAdditionalFieldsLoading(true);
      Promise.all(
        modelConfig.fields.map((item: any) => {
          const methodName = item.getMethod || 'getAllBaseInformation';
          const call =
            methodName === 'getAllPaginated'
              ? item.service[methodName](500, 0)
              : item.service[methodName]();
          return call.then((res) => ({
            name: item.name,
            code: item.code,
            options: item.getMethod ? res?.results ?? [] : res ?? [],
            optionLabelField: item?.optionLabelField,
            optionValueField: item?.optionValueField
          }));
        })
      )
        .then((updatedFields) => {
          if (!cancelled) {
            setAdditionalFilter(updatedFields);
          }
        })
        .catch(() => {
          if (!cancelled) {
            toast.error(t('pages.versions.loading_filter_options_error'));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setAdditionalFieldsLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    const service = modelConfig.service;
    if (!service) {
      setOptionLabelField(undefined);
      return () => {
        cancelled = true;
      };
    }

    setObjectOptionsLoading(true);
    const finishObjects = (results: Option[]) => {
      if (!cancelled) {
        setObjectOptions(results);
        if (results.length === 0) {
          toast.info('Nessun oggetto disponibile per questo modello');
        }
      }
    };

    if (modelConfig.getMethod) {
      service
        .getAllPaginated(500, 0)
        .then((res) => finishObjects(res?.results ?? []))
        .catch(() => {
          if (!cancelled) {
            toast.error(t('pages.versions.loading_filter_options_error'));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setObjectOptionsLoading(false);
          }
        });
    } else {
      service
        .getAllBaseInformation()
        .then((res) => finishObjects(res ?? []))
        .catch(() => {
          if (!cancelled) {
            toast.error(t('pages.versions.loading_filter_options_error'));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setObjectOptionsLoading(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [selectedModel, t]);

  const selectedModelConfig = selectedModel ? MODELS_MAPS?.[selectedModel.code] : undefined;
  const usesAdditionalFields = Boolean(selectedModelConfig?.fields);

  const handleSubmit = async (values: any) => {
    const hasModel = Boolean(String(values?.model ?? '').trim());
    const hasActor = Boolean(String(values?.actor_name ?? '').trim());
    if (!hasModel && !hasActor) {
      toast.error('Valorizza almeno Modello o Nome utente.');
      return;
    }
    onSubmit(values);
  };

  return (
    <Stack spacing={2}>
      <EnhancedForm
        onSubmit={(values: any) => handleSubmit(values)}
        formId="filterLogs"
        costumControls={
          <FormActions
            searchLabel={t('actions.search')}
            onResetExtra={() => {
              setSelectedModel(null);
              setObjectOptions([]);
              setAdditionalFilter([]);
              setObjectOptionsLoading(false);
              setAdditionalFieldsLoading(false);
            }}
          />
        }
        autoStyleLayout>
        <IdentityField
          name="actor_name"
          label="Nome utente"
          component={TextFieldAdapter}
          type="text"
        />

        <IdentityField
          name="model"
          component={AutocompleteAdapter}
          options={models}
          optionLabel="label"
          optionValue="code"
          label={t('global.model')}
          setValue={setSelectedModel}
          value={selectedModel}
        />

        {usesAdditionalFields &&
          (additionalFieldsLoading ? (
            <Box
              className="flex items-center gap-2 py-2"
              sx={{ minHeight: (theme) => theme.spacing(7) }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {t('pages.versions.loading_filter_options')}
              </Typography>
            </Box>
          ) : (
            additionalFilter.map((item: AdditionalField, index: number) => (
              <>
                <IdentityField
                  name={item.code}
                  label={item.name}
                  component={AutocompleteAdapter}
                  options={item.options}
                  optionLabel={item.optionLabelField ?? 'description'}
                  optionValue={item.optionValueField ?? 'id'}
                  type="text"
                />
              </>
            ))
          ))}
        {selectedModel && !usesAdditionalFields && (
          <>
            {objectOptionsLoading ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minHeight: (theme) => theme.spacing(7) }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  {t('pages.versions.loading_objects')}
                </Typography>
              </Stack>
            ) : objectOptions.length > 0 ? (
              <IdentityField
                name="object"
                label={t('global.object')}
                component={AutocompleteAdapter}
                options={objectOptions}
                optionLabel={optionLabelField ?? 'description'}
                optionValue={MODELS_MAPS?.[selectedModel?.code]?.optionValueField ?? 'id'}
                type="text"
              />
            ) : (
              <IdentityField
                name="object"
                label={t('global.object')}
                component={TextFieldAdapter}
                type="text"
                placeholder="ID o valore"
              />
            )}
          </>
        )}
      </EnhancedForm>
    </Stack>
  );
}

export default LogsFilterForm;
